import { useEffect, useState } from "react";
import Seo from "@/lib/Seo";
import { api, formatApiError } from "@/lib/api";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-sm space-y-6 border border-ink/10 bg-parch p-8" data-testid="admin-login-form">
      <div>
        <p className="mv-label">Owner access</p>
        <h1 className="mt-2 font-display text-4xl text-ink">Welcome back</h1>
        <p className="mt-2 text-sm text-smoke">Sign in to review inquiries and edit the catalog.</p>
      </div>
      <div>
        <label htmlFor="admin-email" className="mv-label">Email</label>
        <input id="admin-email" data-testid="admin-email-input" type="email" className="mv-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
      </div>
      <div>
        <label htmlFor="admin-password" className="mv-label">Password</label>
        <input id="admin-password" data-testid="admin-password-input" type="password" className="mv-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      </div>
      {error && <p role="alert" className="border border-red-800/30 bg-red-50 p-3 text-sm text-red-900" data-testid="admin-login-error">{error}</p>}
      <button type="submit" data-testid="admin-login-submit" disabled={busy} className="w-full rounded-none bg-olive py-3.5 text-[12px] uppercase tracking-[0.18em] text-ivory disabled:opacity-60">
        {busy ? "Signing in…" : "Sign In"}
      </button>
    </form>
  );
}

function InquiryRow({ inq, onStatus }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b border-ink/10">
      <button onClick={() => setOpen(!open)} data-testid={`admin-inquiry-toggle-${inq.inquiry_id}`} className="flex w-full items-center justify-between gap-4 py-4 text-left">
        <span className="min-w-0">
          <span className="block text-sm text-ink">{inq.inquiry_id} · {inq.name} · {inq.email}</span>
          <span className="block text-[12px] text-smoke">{new Date(inq.created_at).toLocaleString()} · {inq.event_date} · {inq.venue_city}</span>
        </span>
        <span className="flex-none text-[11px] uppercase tracking-widest2 text-olive">{inq.status}</span>
      </button>
      {open && (
        <div className="pb-6 text-sm leading-relaxed text-smoke" data-testid={`admin-inquiry-detail-${inq.inquiry_id}`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mv-label">Contact</p>
              <p>Phone: {inq.phone || "—"} {inq.phone ? (inq.phone_contact_ok ? "(ok to call/text)" : "(not confirmed)") : ""}</p>
              <p>Preferred method: {inq.preferred_contact}</p>
              <p>Guest count: {inq.guest_count || "—"}</p>
              <p>Budget: {inq.budget || "—"}</p>
              <p>Marketing opt-in: {inq.marketing_permission ? "yes" : "no"}</p>
            </div>
            <div>
              <p className="mv-label">Interests</p>
              <p>{(inq.interests || []).join(", ") || "—"}</p>
              <p className="mv-label mt-3">Quote items</p>
              {inq.quote_items?.length ? (
                <ul>{inq.quote_items.map((i) => <li key={i.slug}>{i.title} ×{i.quantity}{i.options ? ` — ${i.options}` : ""}</li>)}</ul>
              ) : "—"}
              <p className="mv-label mt-3">Palette / Monogram</p>
              <p>{inq.palette_name || "—"}{inq.monogram ? ` · ${inq.monogram}` : ""}</p>
            </div>
          </div>
          <p className="mv-label mt-4">Details</p>
          <p>Style: {inq.style_colors || "—"} · Quantities: {inq.quantities || "—"}</p>
          <p>Personalization: {inq.personalization || "—"}</p>
          <p>Inspiration: {inq.inspiration_links || "—"}</p>
          <p>Hours: {inq.service_hours || "—"} · Floor: {inq.floor_dimensions || "—"}</p>
          <p>Needed by: {inq.product_needed_by_date || "—"} · Delivery area: {inq.delivery_area || "—"}</p>
          <p>Referral: {inq.referral_source || "—"}</p>
          <p className="mt-2">Additional: {inq.additional_details || "—"}</p>
          <div className="mt-4 flex items-center gap-3">
            <label className="mv-label" htmlFor={`status-${inq.inquiry_id}`}>Status</label>
            <select
              id={`status-${inq.inquiry_id}`}
              data-testid={`admin-inquiry-status-${inq.inquiry_id}`}
              value={inq.status}
              onChange={(e) => onStatus(inq.inquiry_id, e.target.value)}
              className="border border-ink/20 px-3 py-1.5 text-sm"
            >
              <option value="new">new</option>
              <option value="reviewed">reviewed</option>
              <option value="replied">replied</option>
            </select>
          </div>
        </div>
      )}
    </li>
  );
}

function CatalogEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/catalog");
      setItems(data);
    } catch {
      setNote("Could not load catalog.");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (slug) => {
    setNote("");
    try {
      const { data } = await api.patch(`/admin/catalog/${slug}`, {
        title: draft.title,
        description: draft.description,
        published: draft.published,
        availability_note: draft.availability_note,
      });
      setItems((prev) => prev.map((i) => (i.slug === slug ? data : i)));
      setEditing(null);
      setNote(`Saved ${slug}.`);
    } catch (e) {
      setNote(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-catalog-editor">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-3xl text-ink">Catalog</h2>
        <p className="text-[12px] text-smoke">Titles, copy, images, publication state.</p>
      </div>
      {note && <p className="mt-3 text-sm text-olive" data-testid="admin-catalog-note">{note}</p>}
      <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
        {items.map((i) => (
          <li key={i.slug} className="py-4" data-testid={`admin-catalog-row-${i.slug}`}>
            {editing === i.slug ? (
              <div className="space-y-3">
                <input aria-label="Title" data-testid={`admin-edit-title-${i.slug}`} className="mv-input" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                <textarea aria-label="Description" data-testid={`admin-edit-description-${i.slug}`} rows={3} className="mv-input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                <input aria-label="Image URL" data-testid={`admin-edit-image-${i.slug}`} className="mv-input" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} />
                <input aria-label="Availability note" data-testid={`admin-edit-availability-${i.slug}`} className="mv-input" value={draft.availability_note} onChange={(e) => setDraft({ ...draft, availability_note: e.target.value })} placeholder="Availability note (optional)" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" data-testid={`admin-edit-published-${i.slug}`} checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} className="h-4 w-4 accent-[#67672D]" />
                  Published (visible in shop)
                </label>
                <div className="flex gap-3">
                  <button data-testid={`admin-save-${i.slug}`} onClick={() => save(i.slug)} className="rounded-none bg-olive px-6 py-2 text-[11px] uppercase tracking-[0.16em] text-ivory">Save</button>
                  <button data-testid={`admin-cancel-${i.slug}`} onClick={() => setEditing(null)} className="border border-ink/20 px-6 py-2 text-[11px] uppercase tracking-[0.16em]">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={i.image} alt="" className="h-14 w-11 flex-none object-cover" width="44" height="56" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink">{i.title} {!i.published && <span className="ml-2 text-[10px] uppercase tracking-widest2 text-red-800">unpublished</span>}</p>
                  <p className="truncate text-[12px] text-smoke">{i.slug} · {i.category}</p>
                </div>
                <button
                  data-testid={`admin-edit-${i.slug}`}
                  onClick={() => { setEditing(i.slug); setDraft({ title: i.title, description: i.description, image: i.image, published: i.published, availability_note: i.availability_note || "" }); }}
                  className="flex-none border border-ink/20 px-5 py-2 text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:border-olive hover:text-olive"
                >
                  Edit
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = checking
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState(null);
  const [loadNote, setLoadNote] = useState("");

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  const loadInquiries = async () => {
    setLoadNote("");
    try {
      const { data } = await api.get("/admin/inquiries");
      setInquiries(data);
    } catch (e) {
      setLoadNote(formatApiError(e));
    }
  };
  useEffect(() => {
    if (user) loadInquiries();
  }, [user]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    setInquiries((prev) => prev.map((i) => (i.inquiry_id === id ? { ...i, status } : i)));
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "marble-veil-inquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setLoadNote(formatApiError(e));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <div className="pt-[72px]">
      <Seo title="Owner Admin — Marble & Veil" description="Owner access." path="/admin" />
      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8 lg:py-24" data-testid="admin-page">
        {user === undefined && <p className="text-smoke">Checking your session…</p>}
        {user === null && <Login onLogin={setUser} />}
        {user && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="mv-label">Owner Admin</p>
                <h1 className="mt-2 font-display text-4xl text-ink">Hello, {user.name}</h1>
              </div>
              <button data-testid="admin-logout" onClick={logout} className="border border-ink/20 px-6 py-2.5 text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:border-olive hover:text-olive">
                Sign Out
              </button>
            </div>

            <div className="mt-8 flex gap-2">
              {[
                ["inquiries", "Inquiries"],
                ["catalog", "Catalog"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  data-testid={`admin-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`border px-6 py-2.5 text-[11px] uppercase tracking-[0.16em] transition-colors ${tab === key ? "border-olive bg-olive text-ivory" : "border-ink/20 text-ink hover:border-olive hover:text-olive"}`}
                >
                  {label}
                </button>
              ))}
              <button data-testid="admin-export-csv" onClick={exportCsv} className="ml-auto border border-ink/20 px-6 py-2.5 text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:border-olive hover:text-olive">
                Export CSV
              </button>
            </div>

            {loadNote && <p role="alert" className="mt-4 border border-red-800/30 bg-red-50 p-3 text-sm text-red-900" data-testid="admin-load-error">{loadNote}</p>}

            <div className="mt-8">
              {tab === "inquiries" && (
                <div data-testid="admin-inquiries-panel">
                  {!inquiries && <p className="text-smoke">Loading inquiries…</p>}
                  {inquiries && inquiries.length === 0 && (
                    <p className="text-smoke" data-testid="admin-inquiries-empty">
                      No inquiries yet. When a couple submits the form, their inquiry appears here with a unique ID and everything they shared.
                    </p>
                  )}
                  {inquiries && inquiries.length > 0 && (
                    <ul className="divide-y divide-ink/10 border-b border-ink/10" data-testid="admin-inquiries-list">
                      {inquiries.map((i) => (
                        <InquiryRow key={i.inquiry_id} inq={i} onStatus={setStatus} />
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {tab === "catalog" && <CatalogEditor />}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
